import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Discourages screenshots, recordings, copying and printing of CRM data.
 * Wraps the whole app (main.tsx).
 *
 * A web page cannot actually stop a screenshot: the operating system takes
 * it, and Windows (Win+Shift+S) and macOS (Cmd+Shift+3/4/5) handle their
 * shortcuts before the page can react. What a page can do is go black early
 * and stay black while a capture tool is likely to be open:
 *
 *   - Windows key or Print Screen: black, and it STAYS black until the window
 *     is focused again or the cover is clicked. Snipping overlays take focus,
 *     so the snip sees the black cover.
 *   - Cmd+Shift on a Mac: black while held. Cmd alone (Cmd+C, Cmd+Tab) is
 *     left alone, because Mac users press it all day.
 *   - Window or tab not focused: black.
 *   - Copy, cut, drag, right-click, print, save page: blocked outside the
 *     fields people type in; printing renders a blank page.
 */
export function ScreenGuard({ children }: { children: ReactNode }) {
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && !document.hasFocus());
  // Set by the Windows key / Print Screen; only focus or a click clears it.
  const sticky = useRef(false);

  useEffect(() => {
    const isMac = /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);

    const isEditable = (t: EventTarget | null) =>
      t instanceof HTMLElement && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));
    // Phone numbers (components/CopyPhone) are the one thing people may copy.
    const inAllowed = (n: Node | null | undefined) =>
      !!(n && (n instanceof Element ? n : n.parentElement)?.closest('[data-allow-copy]'));
    const selectionAllowed = () => {
      const sel = document.getSelection();
      return !!sel && !sel.isCollapsed && inAllowed(sel.anchorNode) && inAllowed(sel.focusNode);
    };

    const wipeClipboard = () => {
      try {
        void navigator.clipboard?.writeText('');
      } catch {
        /* no clipboard permission: nothing more a page can do */
      }
    };

    const hideSticky = () => {
      sticky.current = true;
      setHidden(true);
    };
    const reveal = () => {
      sticky.current = false;
      setHidden(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const k = e.key;
      if (k === 'PrintScreen') {
        hideSticky();
        wipeClipboard();
        return;
      }
      if (!isMac && (k === 'Meta' || k === 'OS' || e.metaKey)) {
        hideSticky();
      } else if (isMac && e.metaKey && e.shiftKey) {
        setHidden(true);
      }

      const mod = e.ctrlKey || e.metaKey;
      const lower = k.toLowerCase();
      // Print, save page, view source.
      if (mod && (lower === 'p' || lower === 's' || lower === 'u')) {
        e.preventDefault();
        return;
      }
      // Copy / cut / select-all outside the fields people type in.
      if (mod && (lower === 'c' || lower === 'x' || lower === 'a') && !isEditable(e.target) && !(lower === 'c' && selectionAllowed())) {
        e.preventDefault();
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'PrintScreen') {
        // Windows often delivers only the keyup for Print Screen.
        hideSticky();
        wipeClipboard();
        return;
      }
      if (!isMac && (e.key === 'Meta' || e.key === 'OS')) {
        hideSticky();
        return;
      }
      // Mac: Cmd+Shift released and nothing sticky pending.
      if (isMac && !e.metaKey && !sticky.current && document.hasFocus()) setHidden(false);
    };

    const block = (e: Event) => {
      if (e.type === 'contextmenu' && inAllowed(e.target as Node)) return;
      e.preventDefault();
    };
    const onCopy = (e: ClipboardEvent) => {
      if (isEditable(e.target) || (e.type === 'copy' && selectionAllowed())) return;
      e.preventDefault();
      e.clipboardData?.setData('text/plain', '');
    };
    const onBlur = () => setHidden(true);
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') setHidden(true);
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', reveal);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('copy', onCopy, true);
    document.addEventListener('cut', onCopy, true);
    document.addEventListener('contextmenu', block, true);
    document.addEventListener('dragstart', block, true);
    window.addEventListener('beforeprint', onBlur);
    document.body.classList.add('screen-guard');

    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', reveal);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('copy', onCopy, true);
      document.removeEventListener('cut', onCopy, true);
      document.removeEventListener('contextmenu', block, true);
      document.removeEventListener('dragstart', block, true);
      window.removeEventListener('beforeprint', onBlur);
      document.body.classList.remove('screen-guard');
    };
  }, []);

  return (
    <>
      {children}
      {hidden && (
        <div
          className="screen-guard-cover"
          onClick={() => {
            sticky.current = false;
            setHidden(false);
          }}
        >
          <div>
            <strong>Content hidden</strong>
            <span>CRM data is protected. Click anywhere to continue.</span>
          </div>
        </div>
      )}
    </>
  );
}
